import { ArticleConfig } from '@/common/api/models.pleased';
import { EventType } from '.';
import { MessageType, SystemOptionType } from '../enum';
import {
  ArticleSystemOption,
  BaseEvent,
  BaseExternalEvent,
  BaseMessage,
  ConnectToAgent,
  MessageListItem,
  NoneOfTheAbove,
  ResolutionConfig,
  SystemOptionEvent,
} from '../types';
import { isInteractiveMessage } from './ItemType';
import { toConnectArticleId } from './connectArticleId.utils';

interface MessageListItemLike extends Partial<MessageListItem> {}

export function getInteractivePart(message: BaseMessage, part: 0 | 1, def = '') {
  return isInteractiveMessage(message) ? message.interactiveMessageParts[part] : def;
}

/**
 * Build a message history for the new message.
 * @param event The event to create the message history from.
 * @param historicMessage The message that triggered this event.
 * @param append Any messages to append to the history for information purposes.
 * @returns An array of message history.
 */
export function createMessageHistory(
  event: BaseEvent | BaseExternalEvent,
  historicMessage: BaseMessage,
  ...append: BaseMessage[]
) {
  const { history = [] } = historicMessage;
  return [...history, { message: historicMessage, event }, ...append.map((m) => ({ message: m }))];
}

/**
 * Similar to `Array.findIndex`, returns index if the message exists, or returns `-1` if does not exist.
 * @param messages List of messages.
 * @param toFind The message object to find.
 */
export function findMessageIndex(messages: MessageListItem[], toFind: MessageListItemLike) {
  return messages.findIndex((m) => isMessageEqual(m, toFind));
}

/**
 * Works like `Array.filter` to remove a message from the list of messages.
 * @param messages The messages to delete from.
 * @param toDelete The object to delete.
 * @returns A new list of messages with the message removed.
 */
export function deleteMessage(messages: MessageListItem[], toDelete: MessageListItemLike) {
  return messages.filter((m) => !isMessageEqual(m, toDelete));
}

/**
 * Sort the messages if they are out of order.
 * @param messages The list of message to sort.
 * @returns Sorted according to sticky or messages.
 */
export function sortMessageItems<T extends MessageListItemLike>(messages: T[]) {
  return [...messages].sort(compareMessageItems);
}

/**
 * Compare messages according to their timestamp and their sticky status.
 */
export function compareMessageItems(message1: MessageListItemLike, message2: MessageListItemLike) {
  if (message1 === message2) return 0;
  if (message1.isSticky && !message2.isSticky) return 1;
  if (message2.isSticky && !message1.isSticky) return -1;
  const [time1, time2] = [message1.timestamp || 0, message2.timestamp || 0];
  if (time1 === time2) return 0;
  return time1 > time2 ? 1 : -1;
}

/**
 * Compare two messages to check if they are equal. This follows the specification in {@link MessageListItem#id}.
 * @see MessageListItem#id The specification is implemented here.
 */
export function isMessageEqual(lhs: MessageListItemLike, rhs: MessageListItemLike) {
  return lhs === rhs || (lhs.id && rhs.id && lhs.id === rhs.id);
}

/**
 * Returns `true` if the message exists.
 * @param messages The messages "haystack"
 * @param toFind The "needle" to find
 */
export function hasMessage(messages: MessageListItem[], toFind: MessageListItemLike) {
  return findMessageIndex(messages, toFind) >= 0;
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryOptionFromMessageHistory(
  history: BaseMessage['history'],
  defaultValue: object | null = null
) {
  return (
    (
      history?.find((h) => EventType.isOptionEvent(h.event) && h.message.type === MessageType.CATEGORIES_LIST)
        ?.event as SystemOptionEvent
    )?.payload?.selected || defaultValue
  );
}

/**
 * Get the selected category from the message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getCategoryTitleFromMessageHistory(history: BaseMessage['history'], defaultValue = '') {
  return getCategoryOptionFromMessageHistory(history, {})?.title || defaultValue;
}

/**
 * Get the title of the section that was last selected from message history.
 * @param history The history to search for
 * @param defaultValue The default value to return if not found.
 */
export function getSectionTitleFromMessageHistory(history: BaseMessage['history'], defaultValue = '') {
  // Find the last selected section title
  const option: null | SystemOptionEvent = [...(history || [])]
    .reverse()
    .find((h) => EventType.isOptionEvent(h.event) && h.message.type === MessageType.SECTION_LIST)
    ?.event as SystemOptionEvent;
  return option?.payload?.selected?.title || defaultValue;
}

export function buildResolution(source: { showEndMessage?: boolean; endMessage?: string }): ResolutionConfig {
  return {
    show: !!source.showEndMessage,
    message: source.endMessage || '',
  };
}

export function buildConnectConfig(source: {
  /** Normalised bot shape (e.g. chat flow); preferred when boolean. */
  connect?: boolean;
  connectToAgent?: boolean;
  loginRequired?: boolean;
  liveChat?: boolean;
  showLiveChat?: boolean;
  openRequest?: boolean;
  showOpenRequest?: boolean;
  voiceChat?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  skipAiChat?: boolean;
  showDescriptionBox?: boolean | null;
}): ConnectToAgent {
  const connect = typeof source.connect === 'boolean' ? source.connect : !!source.connectToAgent;
  const liveChat = typeof source.liveChat === 'boolean' ? source.liveChat : !!source.showLiveChat;
  const openRequest = typeof source.openRequest === 'boolean' ? source.openRequest : !!source.showOpenRequest;
  const voiceChat = typeof source.voiceChat === 'boolean' ? source.voiceChat : !!source.showVoiceChat;

  return {
    connect,
    loginRequired: !!source.loginRequired,
    liveChat,
    openRequest,
    voiceChat,
    transactionMessage: source.transactionMessage,
    skipAiChat: !!source.skipAiChat,
    showDescriptionBox: !!source.showDescriptionBox,
  };
}

export function buildNoneOfTheAboveConfig(source: {
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  showToEndUser: boolean;
  noneOfTheAbove: boolean;
  responseMessage?: string;
  showEndMessage?: boolean;
  endMessage?: string;
}): NoneOfTheAbove {
  return {
    show: source.noneOfTheAbove && source.showToEndUser,
    showResponseMessage: !!source.responseMessage,
    responseMessage: source.responseMessage,
    config: {
      connect: buildConnectConfig(source),
      resolution: buildResolution(source),
    },
  };
}

/** Drop `id`/`kind` and UI connect flags; remap flags via buildConnectConfig. */
const toArticleConnectFields = (option: ArticleSystemOption) => {
  const { id, kind, connectToAgent, loginRequired, showLiveChat, showOpenRequest, showVoiceChat, ...fields } = option;

  return fields;
};

export function buildArticleOption(source: ArticleConfig): ArticleSystemOption {
  const id = source.articleId ?? source.id;
  const articleId = toConnectArticleId({ id, articleId: source.articleId });

  return {
    kind: SystemOptionType.ARTICLE,
    id,
    articleId,
    title: source.title,

    hideContent: Boolean(source.hideContent),
    transactionType: source.transactionType || '',
    transactionTypes: source.transactionTypes || [],
    showDescriptionBox: source.showDescriptionBox ?? true,
    descriptionMessage: source.descriptionMessage || '',

    overrideParentConnectAgent: Boolean(source.overrideParentConnectAgent),
    connectToAgent: Boolean(source.connectToAgent),
    loginRequired: Boolean(source.loginRequired),
    showLiveChat: Boolean(source.showLiveChat),
    showOpenRequest: Boolean(source.showOpenRequest),
    showVoiceChat: Boolean(source.showVoiceChat),

    showConnectAgentMessage: Boolean(source.showConnectAgentMessage),
    connectAgentMessage: source.connectAgentMessage || '',

    enableAbTesting: Boolean(source.enableAbTesting),
    abValue: source.abValue || '',
    enableMetadataRules: source.enableMetadataRules || false,
    metadataRules: source.metadataRules || [],

    // we need to keep it boolean | null/undefined to determine if we should use the parent configuration
    skipAiChat: source.skipAiChat,

    triggerTicketCreation: !!source.triggerTicketCreation,
    ticketCreationResponseMessage: source.ticketCreationResponseMessage || '',
    ...(source.checks ? { checks: source.checks } : {}),
    ...(source.checkActionId ? { checkActionId: source.checkActionId } : {}),
    ...(source.action
      ? {
          action: {
            action: source.action,
            actionMessage: source.actionMessage || '',
            actionTitle: source.actionText || '',
          },
        }
      : {}),

    category: source.category || '',
    section: source.section || '',
  };
}

export function buildConnectToAgentWithArticleConfig(
  source: ConnectToAgent,
  articleConfig: ArticleSystemOption
): ConnectToAgent {
  // We need to check for parent skipAiChat configuration if article one not selected
  const skipAiChat = articleConfig.skipAiChat ?? source.skipAiChat;
  const articleId = articleConfig.articleId ?? toConnectArticleId(articleConfig);
  const articleFields = toArticleConnectFields(articleConfig);

  if (articleConfig.overrideParentConnectAgent) {
    const articleConnectConfig = buildConnectConfig(articleConfig);

    return {
      ...articleFields,
      ...articleConnectConfig,
      skipAiChat,
      articleId,
    };
  }

  // If the article does not override the parent connect agent, then use the parent connect agent configuration.
  const merged = {
    ...source,
    ...articleFields,
    skipAiChat,
    articleId,
  };

  // Parent `ArticleParentConfig` uses connectToAgent / showLiveChat; canConnectToAgent() expects connect / liveChat.
  return {
    ...merged,
    ...buildConnectConfig(merged),
  };
}

export function mergeConnectConfig(source: ConnectToAgent, override: Partial<ConnectToAgent>): ConnectToAgent {
  return {
    ...source,
    ...override,
  };
}
